import React, { useMemo, useState, useEffect } from "react";
import { Badge, Button } from "react-bootstrap";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { oneKlicks } from "../../../../SDK/oneclick";
import { getuserdata } from "../../../../services/apiService";

const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Specialty template database for custom AI generation
const specialtyTemplates = {
    Dentistry: {
        Posters: [
            { topic: "5 Rules for Healthy Gums", caption: "Healthy gums are the foundation of a bright smile! Brush twice a day, floss daily, eat crunchy vegetables, limit sugary treats, and schedule standard check-ups. #OralHealth #MeddyList #HealthySmile" },
            { topic: "Why Flossing is Mandatory", caption: "Did you know brushing only cleans about 60% of your teeth surfaces? Flossing gets to the other 40% that lies hidden between them! Make flossing a non-negotiable daily habit. #DentalHygiene #MeddyList #DentistTips" },
            { topic: "Foods That Stain Your Teeth", caption: "Love coffee, red wine, berries, or dark sodas? These tasty items are notorious for staining enamel. Rinse with water immediately after consuming them to minimize discoloration! #TeethWhitening #HealthyHabits #Dentist" },
            { topic: "Warning Signs of Cavities", caption: "Don't wait for a toothache! Early warning signs of cavities include mild tooth sensitivity, sharp pain when eating sweet/cold items, visible holes, or dark stains on enamel. See us early! #CavityPrevention #OralCare #MeddyList" },
            { topic: "Importance of Milk Teeth", caption: "Baby teeth aren't just temporary - they hold essential space for permanent teeth, aid proper speech development, and make chewing healthy food easy. Let's start healthy habits early! #PediatricDentistry #KidsHealth #MeddyList" }
        ],
        Videos: [
            { topic: "Perfect Brushing Technique Demo", caption: "Watch our quick circular brushing guide! Angle your toothbrush at 45 degrees towards the gums and brush in gentle circular patterns. Never scrub back and forth! #BrushingTips #HealthyGums #MeddyList" },
            { topic: "Electric vs Manual Toothbrushes", caption: "Which is best for you? Electric toothbrushes offer superior plaque removal and built-in timers, making them excellent for children and arthritic hands. Manual brushes work wonders when used correctly! #DentalGuide #OralHygiene #MeddyList" },
            { topic: "Quick Oral Myths Debunked", caption: "Myth: Bleeding gums mean you should stop flossing. Fact: Bleeding is a sign of inflammation, meaning you need to floss MORE, not less! Let's clear the air. #DentalMyths #OralHealth #DentistInsights" },
            { topic: "FAQ: How to Handle a Dental Emergency", caption: "Knocked out a permanent tooth? Keep it moist in milk or clean saliva and get to our clinic within 60 minutes for the highest chance of saving it! Save this post! #DentalEmergency #FirstAidTips #MeddyList" }
        ],
        Articles: [
            { topic: "Ultimate Guide to Wisdom Teeth", caption: "Are your wisdom teeth causing pain? Read our guide on signs they are coming in, when extraction is recommended, and what to expect during a seamless recovery. #OralSurgery #WisdomTeeth #DentalWellness" },
            { topic: "Why Regular Cleanings Save Money", caption: "Preventive care is always cheaper than restorative treatment. Routine cleanings help catch issues early before they turn into expensive root canals or implants! #DentalPreventive #SmartSpending #MeddyList" }
        ]
    },
    Cardiology: {
        Posters: [
            { topic: "Heart-Healthy Superfoods", caption: "Feed your heart the absolute best! Incorporate leafy greens, berries, walnuts, avocados, fatty fish, and oatmeal into your meals daily to keep arteries clean and flexible. #HeartHealth #Superfoods #HealthyEating" },
            { topic: "Understanding BP Numbers", caption: "What do your blood pressure numbers really mean? Systolic (top) is the pressure when heart beats, and Diastolic (bottom) is between beats. Aim to keep yours under 120/80! #Hypertension #BloodPressure #HeartCare" },
            { topic: "5 Daily Habits for a Strong Heart", caption: "Your daily actions protect your heart! Move for 30 minutes, limit sodium intake, manage chronic stress, sleep 7-8 hours, and never ignore chest discomfort. #HealthyLiving #CardiologyTips #MeddyList" },
            { topic: "The Danger of Trans Fats", caption: "Trans fats raise bad cholesterol (LDL) and lower good cholesterol (HDL), significantly raising heart disease risks. Always check labels for 'partially hydrogenated oils'! #CholesterolManagement #EatClean #MeddyList" }
        ],
        Videos: [
            { topic: "Cardio Exercises for Home", caption: "Keep your heart pumping without a gym! Try jumping jacks, high knees, shadowboxing, and stepping exercises in your living room for a quick, effective 15-minute cardio routine. #HomeWorkout #CardioHealth #StayActive" },
            { topic: "Measuring BP Correctly at Home", caption: "Avoid incorrect blood pressure readings! Sit quietly for 5 minutes, keep your arm supported at heart level, place the cuff on bare skin, and keep feet flat on the floor. #BPGuide #HypertensionTips #MeddyList" },
            { topic: "FAQ: When to See a Cardiologist", caption: "Experiencing shortness of breath, unexplained dizziness, fluttery palpitations, or a family history of early heart disease? It's always best to get checked by a specialist! #HeartSpecialist #Cardiology #HealthFirst" }
        ],
        Articles: [
            { topic: "How Daily Walking Protects Arteries", caption: "A simple brisk walk daily is a powerful tool for your blood vessels. It improves circulation, lowers systemic inflammation, helps manage weight, and keeps blood pressure stable. #WalkingHealth #ActiveLifestyle #MeddyList" },
            { topic: "Genetics vs Lifestyle in Heart Disease", caption: "Your genes load the gun, but your lifestyle pulls the trigger. Discover how consistent positive daily choices can overcome hereditary cardiac risks. #HealthyHeart #GeneticRisk #CardioWellness" }
        ]
    },
    Pediatrics: {
        Posters: [
            { topic: "Childhood Immunization Schedule", caption: "Vaccines are standard safeguards protecting children from life-threatening diseases. Keep your child's immunization schedule updated for a happy, disease-free childhood. #VaccinesSaveLives #ChildHealth #MeddyList" },
            { topic: "Healthy Sleep Guidelines by Age", caption: "Sleep is crucial for growth! Toddlers need 11-14 hours, school-aged kids need 9-11 hours, and teenagers need 8-10 hours. Set structured, screen-free bedtime routines. #KidsSleep #ParentingTips #ChildDevelopment" },
            { topic: "Screen Time Guidelines", caption: "Protect developing eyes and brains! Under 18 months should have no screens, ages 2-5 should be limited to 1 hour of high-quality educational programming. Promote outdoor play! #DigitalHealth #HealthyKids #MeddyList" }
        ],
        Videos: [
            { topic: "Packing a Nutritious School Lunch", caption: "Fuel active minds! Pack a balanced lunch with complex carbs (whole wheat), lean proteins (egg/paneer/beans), fresh fruit, and crunchy veggies. Avoid packaged sugary juices! #SchoolLunchIdeas #KidsNutrition #HealthyEating" },
            { topic: "Soothing a Teething Baby Safely", caption: "Is your little one teething? Gently rub gums with a clean finger, offer chilled (not frozen) silicone teething rings, or use clean damp washcloths. Avoid dangerous amber necklaces! #TeethingTips #NewParents #MeddyList" },
            { topic: "Identifying Child Dehydration Signs", caption: "Watch out during hot weather or illness! Key signs of dehydration include dry mouth, crying with few or no tears, irritability, and having fewer wet diapers. #KidsHealth #PediatricsTips #MeddyList" }
        ],
        Articles: [
            { topic: "Managing Screen Time and Teen Sleep", caption: "Learn why screens in the bedroom disrupt crucial sleep cycles in adolescents, and how to collaborate with your teenager to build balanced digital boundaries. #ParentingTeens #TeenMentalHealth #MeddyList" },
            { topic: "Parent's Guide to Growth Milestones", caption: "Every child grows at their own pace! Read our guide on physical, cognitive, and social milestones for toddlers, and when you should discuss concerns with your pediatrician. #GrowthMilestones #BabyCare #ChildSpecialist" }
        ]
    },
    Dermatology: {
        Posters: [
            { topic: "5 Rules of Daily Sunscreen Use", caption: "Sunscreen is your #1 anti-aging skin protector! Use broad-spectrum SPF 30+, apply two finger lengths for face/neck, wear it indoors, reapply every 2 hours outdoors, and check the expiration date. #SunscreenDaily #SkincareTips #HealthySkin" },
            { topic: "Ideal Skincare Routine Order", caption: "Order matters for maximum product absorption! Apply the thinnest consistency first: Cleanser -> Toner -> Serum -> Eye Cream -> Moisturizer -> Sunscreen (morning). #SkincareRoutine #DermatologistTips #MeddyList" },
            { topic: "Warning Signs of Melanoma", caption: "Perform monthly skin checks using the ABCDE guide: Asymmetry, Border irregularity, Color changes, Diameter > 6mm, and Evolving shape or size. Consult us immediately if you spot a suspect mole! #SkinCancerAwareness #MelanomaCheck #MeddyList" }
        ],
        Videos: [
            { topic: "Correct Way to Apply Serum", caption: "Stop rubbing serums! Press and pat gentle droplets into slightly damp skin. This enhances absorption and prevents micro-tears in delicate skin layers. #SerumApplication #SkincareHacks #Dermatologist" },
            { topic: "Debunking Skincare Myths", caption: "Myth: Hot water shrinks pores. Fact: Pores don't have muscles to open or close! Hot water actually strips natural oils, causing dryness and irritation. Use lukewarm water instead! #SkincareMyths #HealthyGlow #MeddyList" }
        ],
        Articles: [
            { topic: "Complete Guide to Acne Prevention", caption: "Acne is treatable! Dive deep into the causes of breakouts, active ingredients that work (salicylic acid, retinoids), and daily habits that keep your pores clear. #AcneTreatment #ClearSkinGuide #SkincareWellness" },
            { topic: "Sunscreen: Best Anti-Aging Secret", caption: "90% of premature skin aging is caused by UV exposure. Read about how consistent daily sunscreen application stops fine lines, wrinkles, and hyperpigmentation before they start. #AntiAgingSecrets #SunProtection #MeddyList" }
        ]
    },
    Orthopedics: {
        Posters: [
            { topic: "Perfect Posture for Desk Workers", caption: "Save your spine! Keep monitor at eye level, elbows at 90 degrees, lower back supported by a chair lumbar roll, feet flat on the ground, and take dynamic stretch breaks every hour. #Ergonomics #PostureTips #BackPainPrevention" },
            { topic: "5 Exercises for Knee Strength", caption: "Protect your knees by strengthening surrounding muscles! Perform straight leg raises, hamstring curls, wall slides, calf raises, and glute bridges consistently. #KneeHealth #OrthopedicsTips #MeddyList" },
            { topic: "How to Prevent Osteoporosis", caption: "Build strong bones early! Consume adequate calcium (dairy, leafy greens), optimize Vitamin D levels, engage in weight-bearing exercises, and avoid smoking/heavy alcohol. #BoneHealth #OsteoporosisPrevention #MeddyList" }
        ],
        Videos: [
            { topic: "Desk Stretches to Do Hourly", caption: "Stuck at your desk? Take 2 minutes to perform chest openers, neck rolls, wrist flexions, and seated spinal twists. Your muscles will thank you! #DeskStretches #Flexibility #OfficeHealth" },
            { topic: "How to Choose Walking Shoes", caption: "Protect your joints! Look for walking shoes with proper arch support, firm heel counters, ample toe box room, and lightweight shock-absorbing cushioning. #JointHealth #FootwearTips #StayActive" }
        ],
        Articles: [
            { topic: "Benefits of Low-Impact Workouts", caption: "Protect your joints while staying fit! Discover how swimming, cycling, elliptical training, and yoga keep you healthy without high-impact stress on your knees and spine. #LowImpactFitness #JointCare #HealthyAging" },
            { topic: "Understanding and Managing Arthritis", caption: "Arthritis doesn't have to control your life. Read about safe medical treatments, nutrition, and exercise therapies to keep joints flexible and pain-free. #ArthritisManagement #OrthopedicCare #MeddyList" }
        ]
    },
    "General Health": {
        Posters: [
            { topic: "Daily Hydration Guidelines", caption: "Water is vital for energy, concentration, digestion, and clear skin! Aim for 8-10 glasses daily. Keep a reusable water bottle handy and flavor it with fresh lemon or mint. #HydrationTips #DrinkWater #HealthyHabits" },
            { topic: "7 Benefits of 8 Hours of Sleep", caption: "Sleep is non-negotiable! Adequate rest boosts immunity, sharpens mental focus, stabilizes mood, aids weight management, reduces heart risks, and enhances memory consolidation. #SleepHealth #ImmunityBoost #Wellness" },
            { topic: "Why Regular Checkups Save Lives", caption: "Preventive medicine is the best medicine! Routine screening checks catch hypertension, diabetes, and other concerns before symptoms even arise. Book your annual checkup! #PreventiveCare #HealthyLife #MeddyList" }
        ],
        Videos: [
            { topic: "Quick Breathing Exercise for Stress", caption: "Feeling overwhelmed? Practice Box Breathing: inhale for 4 seconds, hold for 4, exhale for 4, hold for 4. Repeat 3 times to quickly reset your nervous system. #StressRelief #Mindfulness #MentalWellness" },
            { topic: "Quick Healthy Snack Ideas", caption: "Skip processed snacks! Opt for Greek yogurt with berries, a handful of almonds, apple slices with peanut butter, or carrot sticks with hummus for sustained energy. #HealthySnacks #EatClean #MeddyList" }
        ],
        Articles: [
            { topic: "Importance of Annual Physicals", caption: "A comprehensive guide on what key numbers (blood sugar, lipids, kidney function) are tracked in an annual health panel, and why tracking these over time is vital. #GeneralPhysician #AnnualCheckup #HealthyHabits" },
            { topic: "Managing Seasonal Allergies Naturally", caption: "Tired of sneezing? Discover effective medical therapies and natural lifestyle habits to reduce pollen exposure and manage allergy flares successfully. #SeasonalAllergies #HealthGuide #MeddyList" }
        ]
    }
};

function getMonthMatrix(date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);
    const startDay = firstOfMonth.getDay();
    const totalDays = lastOfMonth.getDate();

    const cells = [];
    for (let i = 0; i < startDay; i += 1) {
        cells.push(null);
    }
    for (let d = 1; d <= totalDays; d += 1) {
        cells.push(new Date(year, month, d));
    }
    while (cells.length % 7 !== 0) {
        cells.push(null);
    }
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7));
    }
    return weeks;
}

const MonthlyPostingPlan = ({ userdata = {} }) => {
    const buttonBaseStyle = {
        height: '36px',
        padding: '0 14px',
        fontSize: '13px',
        fontWeight: '600',
        borderRadius: '6px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        border: '1px solid transparent',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        whiteSpace: 'nowrap'
    };

    const navBtnStyle = {
        height: '32px',
        width: '32px',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '6px',
        border: '1px solid #e2e8f0',
        backgroundColor: '#fff',
        color: '#4a5568',
        cursor: 'pointer',
        transition: 'all 0.15s ease'
    };

    const todayBtnStyle = {
        height: '32px',
        padding: '0 12px',
        fontSize: '13px',
        fontWeight: '600',
        borderRadius: '6px',
        border: '1px solid #e2e8f0',
        backgroundColor: '#fff',
        color: '#4a5568',
        cursor: 'pointer',
        transition: 'all 0.15s ease'
    };

    const [query, setQuery] = useState("");
    const [current, setCurrent] = useState(() => new Date());

    // Compute dynamic doctor-specific local storage key
    const storageKey = useMemo(() => {
        return userdata?.id ? `monthly_posting_plan_${userdata.id}` : "monthly_posting_plan";
    }, [userdata]);

    // Main local storage database of active posts
    const [posts, setPosts] = useState({});

    // Synchronize posts when the dynamic storageKey changes
    useEffect(() => {
        try {
            const data = localStorage.getItem(storageKey);
            setPosts(data ? JSON.parse(data) : {});
        } catch (e) {
            console.error("Error loading posts from localStorage:", e);
            setPosts({});
        }
    }, [storageKey]);

    // Form modal state
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formDate, setFormDate] = useState("");
    const [formType, setFormType] = useState("Poster");
    const [formTopic, setFormTopic] = useState("");
    const [formCaption, setFormCaption] = useState("");

    // AI Generation Wizard State
    const [isAIWizardOpen, setIsAIWizardOpen] = useState(false);
    const [aiSpecialty, setAiSpecialty] = useState("General Health");
    const [aiGoal, setAiGoal] = useState("Patient Education");
    const [aiPattern, setAiPattern] = useState("Alternating Poster/Video");

    // Automatically detect and set doctor's specialty dynamically from DB table settings and profile fields
    useEffect(() => {
        const fetchDoctorSpecialties = async () => {
            // First try standard string matching on user profile subheadings/speciality string fields
            if (userdata && (userdata.speciality || userdata.subheading || userdata.sub_heading)) {
                const detected = String(userdata.speciality || userdata.subheading || userdata.sub_heading || "").trim().toLowerCase();
                if (detected.includes("dent") || detected.includes("teeth") || detected.includes("oral")) {
                    setAiSpecialty("Dentistry");
                    return;
                } else if (detected.includes("card") || detected.includes("heart") || detected.includes("vessel") || detected.includes("onc")) {
                    setAiSpecialty("Cardiology");
                    return;
                } else if (detected.includes("ped") || detected.includes("child") || detected.includes("baby")) {
                    setAiSpecialty("Pediatrics");
                    return;
                } else if (detected.includes("derm") || detected.includes("skin") || detected.includes("hair")) {
                    setAiSpecialty("Dermatology");
                    return;
                } else if (detected.includes("orth") || detected.includes("bone") || detected.includes("joint")) {
                    setAiSpecialty("Orthopedics");
                    return;
                }
            }

            // Fallback: Query the doctor_specialities database table directly
            const docId = userdata?.id || localStorage.getItem("doctorId") || await getuserdata('id').catch(() => null);
            if (!docId) return;

            try {
                const specRes = await oneKlicks.read("", "doctor_specialities", { doctor_id: docId }, "id DESC", "", "", "");
                if (specRes.success && specRes.data && specRes.data.length > 0) {
                    const optRes = await oneKlicks.read("", "specialities", {}, "id ASC", "", "", "");
                    if (optRes.success && optRes.data) {
                        const activeSpecIds = specRes.data.map(item => String(item.speciality_id));
                        const matchedOpts = optRes.data.filter(opt => activeSpecIds.includes(String(opt.id)));
                        
                        if (matchedOpts.length > 0) {
                            const detectedSpecName = String(matchedOpts[0].name).trim().toLowerCase();
                            if (detectedSpecName.includes("dent") || detectedSpecName.includes("teeth") || detectedSpecName.includes("oral")) {
                                setAiSpecialty("Dentistry");
                            } else if (detectedSpecName.includes("card") || detectedSpecName.includes("heart") || detectedSpecName.includes("vessel") || detectedSpecName.includes("onc")) {
                                setAiSpecialty("Cardiology");
                            } else if (detectedSpecName.includes("ped") || detectedSpecName.includes("child") || detectedSpecName.includes("baby")) {
                                setAiSpecialty("Pediatrics");
                            } else if (detectedSpecName.includes("derm") || detectedSpecName.includes("skin") || detectedSpecName.includes("hair")) {
                                setAiSpecialty("Dermatology");
                            } else if (detectedSpecName.includes("orth") || detectedSpecName.includes("bone") || detectedSpecName.includes("joint")) {
                                setAiSpecialty("Orthopedics");
                            } else {
                                setAiSpecialty("General Health");
                            }
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to fetch doctor specialties dynamically:", err);
            }
        };

        fetchDoctorSpecialties();
    }, [userdata]);

    const monthLabel = useMemo(() => current.toLocaleString(undefined, { month: "long", year: "numeric" }), [current]);
    const weeks = useMemo(() => getMonthMatrix(current), [current]);

    const goPrev = () => {
        setCurrent((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    };
    const goNext = () => {
        setCurrent((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    };
    const goToday = () => {
        setCurrent(new Date());
    };

    // Excel Export Template Logic
    const handleDownloadTemplate = () => {
        const header = ["Date (YYYY-MM-DD)", "Post Type (Poster/Video/Article)", "Topic/Title", "Caption/Description"];
        const samples = [];
        const today = new Date();

        // 5 pre-filled sample rows
        for (let i = 0; i < 5; i++) {
            const sampleDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
            const dateStr = sampleDate.toISOString().split("T")[0];
            const isVideo = i % 2 === 1;
            samples.push([
                dateStr,
                isVideo ? "Video" : "Poster",
                isVideo ? `Understanding Essential Health Practices - Part ${Math.ceil((i + 1) / 2)}` : `Expert Clinical Topic ${Math.ceil((i + 1) / 2)}`,
                isVideo ? "A quick, step-by-step video outlining standard healthcare guidelines." : "Key update and clinical tips from our specialized care staff. #MeddyList"
            ]);
        }

        const wsData = [header, ...samples];
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, "Posting Plan Template");
        XLSX.writeFile(wb, "MeddyList_Posting_Plan_Template.xlsx");
        toast.success("Excel template downloaded successfully!");
    };

    // Excel Upload Logic
    const handleExcelUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: "binary" });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

                if (data.length <= 1) {
                    toast.error("The uploaded sheet is empty or missing headers.");
                    return;
                }

                const headers = data[0].map(h => String(h).trim().toLowerCase());
                const dateIdx = headers.findIndex(h => h.includes("date"));
                const typeIdx = headers.findIndex(h => h.includes("type"));
                const topicIdx = headers.findIndex(h => h.includes("topic") || h.includes("title"));
                const captionIdx = headers.findIndex(h => h.includes("caption") || h.includes("desc"));

                if (dateIdx === -1 || typeIdx === -1 || topicIdx === -1) {
                    toast.error("Headers must include: Date, Post Type, and Topic/Title");
                    return;
                }

                const updated = { ...posts };
                let importedCount = 0;

                for (let r = 1; r < data.length; r++) {
                    const row = data[r];
                    if (!row || row.length === 0) continue;

                    let dateVal = row[dateIdx];
                    let typeVal = row[typeIdx];
                    let topicVal = row[topicIdx];
                    let captionVal = row[captionIdx] || "";

                    if (!dateVal || !typeVal || !topicVal) continue;

                    // Handle Excel numerical date formats
                    if (typeof dateVal === "number") {
                        const excelEpoch = new Date(1899, 11, 30);
                        const parsedDate = new Date(excelEpoch.getTime() + dateVal * 24 * 60 * 60 * 1000);
                        dateVal = parsedDate.toISOString().split("T")[0];
                    } else {
                        dateVal = String(dateVal).trim();
                        const matchDMY = dateVal.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
                        if (matchDMY) {
                            dateVal = `${matchDMY[3]}-${matchDMY[2].padStart(2, '0')}-${matchDMY[1].padStart(2, '0')}`;
                        }
                    }

                    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) continue;

                    let cleanType = String(typeVal).trim().charAt(0).toUpperCase() + String(typeVal).trim().slice(1).toLowerCase();
                    if (!["Poster", "Video", "Article"].includes(cleanType)) {
                        cleanType = "Poster";
                    }

                    updated[dateVal] = {
                        type: cleanType,
                        topic: String(topicVal).trim(),
                        caption: String(captionVal).trim()
                    };
                    importedCount++;
                }

                setPosts(updated);
                localStorage.setItem(storageKey, JSON.stringify(updated));
                toast.success(`Successfully imported ${importedCount} posts from Excel!`);
            } catch (err) {
                console.error(err);
                toast.error("Failed to parse Excel file. Check column headers.");
            }
        };
        reader.readAsBinaryString(file);
        e.target.value = ""; // Reset file input
    };

    // AI Dynamic Posting Planner Generation
    const handleAIGenerator = () => {
        const selectedSpecialty = specialtyTemplates[aiSpecialty] || specialtyTemplates["General Health"];
        const year = current.getFullYear();
        const month = current.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const updated = { ...posts };
        let generatedCount = 0;

        for (let d = 1; d <= daysInMonth; d++) {
            const activeDate = new Date(year, month, d);
            const dateStr = activeDate.toISOString().split("T")[0];

            let postType = "Poster";
            if (aiPattern === "Alternating Poster/Video") {
                postType = (d % 2 === 1) ? "Poster" : "Video";
            } else if (aiPattern === "Alternating 3-Day Cycle") {
                const mod = d % 3;
                postType = mod === 1 ? "Poster" : mod === 2 ? "Video" : "Article";
            } else if (aiPattern === "Daily Posters") {
                postType = "Poster";
            } else if (aiPattern === "Daily Videos") {
                postType = "Video";
            }

            // Retrieve from specialty library based on modulo indexing
            const typeKey = postType + "s";
            const templatesArray = selectedSpecialty[typeKey] || specialtyTemplates["General Health"][typeKey];
            const tIndex = (d - 1) % templatesArray.length;
            const template = templatesArray[tIndex];

            // Customize goal prefixes/CTAs dynamically
            let finalCaption = template.caption;
            if (aiGoal === "Appointment Bookings") {
                finalCaption = `📅 Ready to optimize your health? Book a convenient consultation with us today! Call us or tap our bio link. \n\n${template.caption}`;
            } else if (aiGoal === "Clinic Promotion") {
                finalCaption = `🏥 MeddyList Certified Clinic Partner. Welcoming new patients this week! \n\n${template.caption}`;
            }

            updated[dateStr] = {
                type: postType,
                topic: template.topic,
                caption: finalCaption
            };
            generatedCount++;
        }

        setPosts(updated);
        localStorage.setItem(storageKey, JSON.stringify(updated));
        setIsAIWizardOpen(false);
        toast.success(`Successfully generated a custom ${generatedCount}-day plan for ${aiSpecialty}!`);
    };

    // Clear entire calendar planner
    const handleClearCalendar = () => {
        if (window.confirm("Are you sure you want to clear your current posting plan calendar?")) {
            setPosts({});
            localStorage.removeItem(storageKey);
            toast.success("Calendar cleared successfully!");
        }
    };

    // Grid filter matching
    const filteredWeeks = useMemo(() => {
        if (!query.trim()) return weeks;
        const lowercaseQuery = query.toLowerCase();

        return weeks.map(week =>
            week.map(day => {
                if (!day) return null;
                const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
                const dayPost = posts[dateStr];
                if (dayPost) {
                    if (dayPost.topic.toLowerCase().includes(lowercaseQuery) || dayPost.caption.toLowerCase().includes(lowercaseQuery) || dayPost.type.toLowerCase().includes(lowercaseQuery)) {
                        return day;
                    }
                }
                return null; // hide days not matching search query
            })
        );
    }, [weeks, posts, query]);

    // Handle day cell click
    const handleDayClick = (day) => {
        if (!day) return;
        const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
        const existingPost = posts[dateStr];

        setFormDate(dateStr);
        if (existingPost) {
            setFormType(existingPost.type);
            setFormTopic(existingPost.topic);
            setFormCaption(existingPost.caption);
        } else {
            setFormType("Poster");
            setFormTopic("");
            setFormCaption("");
        }
        setIsFormOpen(true);
    };

    // Form save handler
    const handleSavePost = () => {
        if (!formTopic.trim()) {
            toast.error("Please enter a topic or title.");
            return;
        }

        const updated = { ...posts };
        updated[formDate] = {
            type: formType,
            topic: formTopic.trim(),
            caption: formCaption.trim()
        };

        setPosts(updated);
        localStorage.setItem(storageKey, JSON.stringify(updated));
        setIsFormOpen(false);
        toast.success(`Post saved successfully!`);
    };

    // Form delete handler
    const handleDeletePost = () => {
        if (window.confirm("Delete this post?")) {
            const updated = { ...posts };
            delete updated[formDate];
            setPosts(updated);
            localStorage.setItem(storageKey, JSON.stringify(updated));
            setIsFormOpen(false);
            toast.success("Post removed.");
        }
    };

    return (
        <div className="container-fluid p-0">
            <div className="row g-3">
                <div className="col-lg-8">
                    {/* Header Card */}
                    <div className="card mb-3 shadow-sm border-0" style={{ borderRadius: '12px' }}>
                        <div className="card-body py-3">
                    <div className="row align-items-center g-3">
                        <div className="col-12 col-lg-4">
                            <div className="d-flex align-items-center gap-2">
                                <h5 className="mb-0 font-weight-bold text-dark" style={{ fontSize: '20px' }}>{monthLabel}</h5>
                                <Badge bg="primary" style={{ backgroundColor: 'var(--themecolor)', fontSize: '12px' }}>Planner</Badge>
                            </div>
                        </div>
                        <div className="col-12 col-lg-8">
                            <div className="d-flex flex-wrap align-items-center gap-2 justify-content-lg-end">
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search topic..."
                                    className="form-control"
                                    style={{ 
                                        maxWidth: 160, 
                                        height: '36px', 
                                        borderRadius: '6px', 
                                        fontSize: '13px',
                                        border: '1px solid #ced4da',
                                        padding: '0 12px'
                                    }}
                                />
                                <Button
                                    size="sm"
                                    onClick={() => setIsAIWizardOpen(true)}
                                    style={{
                                        ...buttonBaseStyle,
                                        background: 'linear-gradient(135deg, var(--themecolor, #11b3e8), #2980b9)',
                                        color: '#fff',
                                        border: 'none'
                                    }}
                                >
                                    <i className="fa fa-magic me-1"></i>AI Planner
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => document.getElementById("excel-upload-input").click()}
                                    style={{
                                        ...buttonBaseStyle,
                                        backgroundColor: '#2ecc71',
                                        color: '#fff',
                                        border: 'none'
                                    }}
                                >
                                    <i className="fa fa-file-excel-o me-1"></i>Import Excel
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={handleDownloadTemplate}
                                    style={{
                                        ...buttonBaseStyle,
                                        backgroundColor: '#fff',
                                        color: 'var(--themecolor, #11b3e8)',
                                        borderColor: 'var(--themecolor, #11b3e8)'
                                    }}
                                >
                                    <i className="fa fa-download me-1"></i>Template
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={handleClearCalendar}
                                    style={{
                                        ...buttonBaseStyle,
                                        padding: 0,
                                        width: '36px',
                                        backgroundColor: '#fff',
                                        color: '#e74c3c',
                                        borderColor: '#e74c3c'
                                    }}
                                >
                                    <i className="fa fa-trash-o" style={{ fontSize: '15px' }}></i>
                                </Button>

                                <input
                                    type="file"
                                    id="excel-upload-input"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleExcelUpload}
                                    style={{ display: "none" }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Card */}
            <div className="card mb-3 shadow-sm border-0" style={{ borderRadius: '12px' }}>
                <div className="card-body py-2">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                size="sm"
                                onClick={goPrev}
                                style={navBtnStyle}
                            >
                                <i className="fa fa-chevron-left text-muted"></i>
                            </Button>
                            <Button
                                size="sm"
                                onClick={goToday}
                                style={todayBtnStyle}
                            >
                                Today
                            </Button>
                            <Button
                                size="sm"
                                onClick={goNext}
                                style={navBtnStyle}
                            >
                                <i className="fa fa-chevron-right text-muted"></i>
                            </Button>
                        </div>
                        <div className="d-flex align-items-center gap-3" style={{ fontSize: '12px' }}>
                            <span className="d-flex align-items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#0c5460' }}></span> Poster</span>
                            <span className="d-flex align-items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#856404' }}></span> Video</span>
                            <span className="d-flex align-items-center gap-1"><span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#383d41' }}></span> Article</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Calendar Card */}
            <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                <div className="card-body p-3">
                    <div className="calendar-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '10px'
                    }}>
                        {/* Day Headers */}
                        {dayNames.map((d) => (
                            <div
                                key={d}
                                className="text-center fw-bold py-2"
                                style={{
                                    color: '#7f8c8d',
                                    fontSize: '13px',
                                    textTransform: 'uppercase',
                                    borderBottom: '2px solid #f2f4f8'
                                }}
                            >
                                {d}
                            </div>
                        ))}

                        {/* Calendar Days */}
                        {filteredWeeks.map((week, wi) => (
                            week.map((day, di) => {
                                if (!day) {
                                    return (
                                        <div
                                            key={`empty-${wi}-${di}`}
                                            style={{
                                                background: '#f8f9fa',
                                                borderRadius: '8px',
                                                minHeight: '105px',
                                                border: '1px dashed #e9ecef'
                                            }}
                                        />
                                    );
                                }

                                const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
                                const dayPost = posts[dateStr];
                                const isToday = new Date().toDateString() === day.toDateString();

                                return (
                                    <div
                                        key={dateStr}
                                        className="calendar-day"
                                        onClick={() => handleDayClick(day)}
                                        style={{
                                            border: isToday ? '2px solid var(--themecolor)' : '1px solid #e9ecef',
                                            borderRadius: '8px',
                                            minHeight: '105px',
                                            padding: '8px',
                                            background: isToday ? '#edf4fc' : '#fff',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.borderColor = 'var(--themecolor)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = 'none';
                                            e.currentTarget.style.transform = 'none';
                                            e.currentTarget.style.borderColor = isToday ? 'var(--themecolor)' : '#e9ecef';
                                        }}
                                    >
                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                            {isToday ? (
                                                <Badge bg="primary" style={{ backgroundColor: 'var(--themecolor)', fontSize: '9px' }}>TODAY</Badge>
                                            ) : <div />}
                                            <div
                                                className="fw-bold"
                                                style={{
                                                    color: isToday ? 'var(--themecolor)' : '#495057',
                                                    fontSize: '14px'
                                                }}
                                            >
                                                {day.getDate()}
                                            </div>
                                        </div>

                                        {/* Post Badge Content */}
                                        {dayPost ? (
                                            <div className="mt-auto" style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                <span
                                                    className="badge text-wrap text-start d-flex align-items-center gap-1 py-1 px-2"
                                                    style={{
                                                        backgroundColor: dayPost.type === 'Video' ? '#fff3cd' : dayPost.type === 'Article' ? '#e2d9f3' : '#d1ecf1',
                                                        color: dayPost.type === 'Video' ? '#856404' : dayPost.type === 'Article' ? '#383d41' : '#0c5460',
                                                        border: dayPost.type === 'Video' ? '1px solid #ffeeba' : dayPost.type === 'Article' ? '1px solid #d6d8db' : '1px solid #bee5eb',
                                                        borderRadius: '4px',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    <i className={`fa ${dayPost.type === 'Video' ? 'fa-play-circle' : dayPost.type === 'Article' ? 'fa-newspaper-o' : 'fa-image'}`}></i>
                                                    <span style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{dayPost.type}</span>
                                                </span>
                                                <div
                                                    className="text-truncate fw-bold mt-1 text-dark"
                                                    style={{ fontSize: '11px', lineHeight: '1.2' }}
                                                    title={dayPost.topic}
                                                >
                                                    {dayPost.topic}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mt-auto text-center text-muted" style={{ fontSize: '10px', opacity: 0.4 }}>
                                                <i className="fa fa-plus-circle"></i> Add
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ))}
                    </div>
                </div>
            </div>

                </div>
                
                {/* Right Side Pane */}
                <div className="col-lg-4">
                    {isFormOpen ? (
                        <div className="card shadow-sm border-0 sticky-top" style={{ borderRadius: '12px', top: '20px' }}>
                            <div className="card-header d-flex align-items-center justify-content-between bg-light py-3 border-0" style={{ borderRadius: '12px 12px 0 0' }}>
                                <h6 className="mb-0 font-weight-bold text-dark" style={{ fontSize: '15px' }}>
                                    <i className="fa fa-edit me-2 text-primary" style={{ color: 'var(--themecolor)' }}></i>
                                    Update Details for {new Date(formDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                </h6>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => setIsFormOpen(false)}
                                    style={{ borderRadius: '50%', width: '28px', height: '28px', padding: 0 }}
                                >
                                    <i className="fa fa-times"></i>
                                </Button>
                            </div>
                            <div className="card-body p-3">
                                <div className="mb-3">
                                    <label className="form-label fw-bold text-dark" style={{ fontSize: '13px' }}>Post Media Type</label>
                                    <select
                                        className="form-select"
                                        value={formType}
                                        onChange={(e) => setFormType(e.target.value)}
                                        style={{ borderRadius: '6px', fontSize: '13px' }}
                                    >
                                        <option value="Poster">🖼️ Poster Graphic</option>
                                        <option value="Video">🎥 Video Content</option>
                                        <option value="Article">📰 Written Article</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold text-dark" style={{ fontSize: '13px' }}>Post Topic / Title</label>
                                    <input
                                        type="text"
                                        value={formTopic}
                                        onChange={(e) => setFormTopic(e.target.value)}
                                        placeholder="Enter what this post is about..."
                                        className="form-control"
                                        style={{ borderRadius: '6px', fontSize: '13px' }}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold text-dark" style={{ fontSize: '13px' }}>Caption / Body Copy</label>
                                    <textarea
                                        value={formCaption}
                                        onChange={(e) => setFormCaption(e.target.value)}
                                        placeholder="Write a highly engaging caption (hashtags, instructions, links)..."
                                        className="form-control"
                                        style={{ minHeight: '180px', borderRadius: '6px', fontSize: '13px' }}
                                    />
                                </div>
                                <div className="d-flex gap-2 justify-content-end mt-4">
                                    {posts[formDate] && (
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            onClick={handleDeletePost}
                                            className="me-auto"
                                            style={{ borderRadius: '6px', fontSize: '13px' }}
                                        >
                                            <i className="fa fa-trash me-1"></i>Delete
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => setIsFormOpen(false)}
                                        style={{ borderRadius: '6px', fontSize: '13px' }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={handleSavePost}
                                        style={{
                                            background: 'var(--themecolor)',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontWeight: '600',
                                            fontSize: '13px',
                                            color: '#fff'
                                        }}
                                    >
                                        <i className="fa fa-save me-1"></i>Save
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm border-0 sticky-top" style={{ borderRadius: '12px', top: '20px' }}>
                            <div className="card-header bg-light py-3 border-0" style={{ borderRadius: '12px 12px 0 0' }}>
                                <h6 className="mb-0 font-weight-bold text-dark" style={{ fontSize: '15px' }}>
                                    <i className="fa fa-info-circle me-2 text-primary" style={{ color: 'var(--themecolor)' }}></i>
                                    Planner Info & Previous Posters
                                </h6>
                            </div>
                            <div className="card-body p-3">
                                <p className="text-muted mb-4" style={{ fontSize: '13px', lineHeight: '1.5' }}>
                                    Click on any date in the calendar to open the update pane and manage your work details.
                                </p>
                                
                                <h6 className="fw-bold mb-3" style={{ fontSize: '14px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>Recent Posters</h6>
                                <div className="d-flex flex-column gap-3">
                                    {Object.keys(posts).length > 0 ? (
                                        Object.entries(posts)
                                            .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                                            .slice(0, 5)
                                            .map(([date, post]) => (
                                                <div key={date} className="p-2 border rounded" style={{ fontSize: '12px', backgroundColor: '#f8fafc', borderColor: '#e2e8f0' }}>
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <strong className="text-primary">{new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</strong>
                                                        <Badge bg="secondary" style={{ fontSize: '10px' }}>{post.type}</Badge>
                                                    </div>
                                                    <div className="fw-bold text-dark mb-1">{post.topic}</div>
                                                    <div className="text-muted text-truncate" style={{ maxWidth: '100%' }}>{post.caption}</div>
                                                </div>
                                            ))
                                    ) : (
                                        <div className="text-center text-muted p-4" style={{ fontSize: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                                            No previous posters found.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* AI Generator Settings Wizard Modal */}
            {isAIWizardOpen && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '20px'
                    }}
                    onClick={() => setIsAIWizardOpen(false)}
                >
                    <div
                        className="card shadow-lg border-0"
                        style={{
                            width: '100%',
                            maxWidth: '560px',
                            borderRadius: '12px',
                            overflow: 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="card-header bg-light d-flex align-items-center justify-content-between py-3 border-0">
                            <h6 className="mb-0 font-weight-bold text-dark" style={{ fontSize: '17px' }}>
                                <i className="fa fa-magic me-2 text-primary" style={{ color: 'var(--themecolor)' }}></i>
                                AI Social Media Planner Wizard
                            </h6>
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => setIsAIWizardOpen(false)}
                                style={{ borderRadius: '50%', width: '28px', height: '28px', padding: 0 }}
                            >
                                <i className="fa fa-times"></i>
                            </Button>
                        </div>
                        <div className="card-body p-4">
                            <div className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center gap-2" style={{ fontSize: '13px', borderRadius: '8px' }}>
                                <i className="fa fa-magic" style={{ fontSize: '18px', color: 'var(--themecolor)' }}></i>
                                <span>AI auto-planner is synced with your clinical specialty: <strong className="text-primary">{aiSpecialty}</strong></span>
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold text-dark" style={{ fontSize: '13px' }}>Primary Marketing Goal</label>
                                <select
                                    className="form-select"
                                    value={aiGoal}
                                    onChange={(e) => setAiGoal(e.target.value)}
                                    style={{ borderRadius: '6px' }}
                                >
                                    <option value="Patient Education">📚 Patient Education & Wellness Awareness</option>
                                    <option value="Appointment Bookings">📅 Promote Services & Boost Appointments</option>
                                    <option value="Clinic Promotion">🏥 Build Trust & Clinic Recognition</option>
                                </select>
                            </div>

                            <div className="mb-0">
                                <label className="form-label fw-bold text-dark" style={{ fontSize: '13px' }}>Monthly Posting Pattern</label>
                                <select
                                    className="form-select"
                                    value={aiPattern}
                                    onChange={(e) => setAiPattern(e.target.value)}
                                    style={{ borderRadius: '6px' }}
                                >
                                    <option value="Alternating Poster/Video">🔄 Alternating (Poster today, Video tomorrow)</option>
                                    <option value="Alternating 3-Day Cycle">🔄 3-Day Cycle (Poster, Video, Article)</option>
                                    <option value="Daily Posters">🖼️ Visual Graphics Only (Daily Posters)</option>
                                    <option value="Daily Videos">🎥 Video Reels Only (Daily Videos)</option>
                                </select>
                            </div>
                        </div>
                        <div className="card-footer bg-light border-0 d-flex gap-2 justify-content-end py-3 px-4">
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => setIsAIWizardOpen(false)}
                                style={{ borderRadius: '6px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleAIGenerator}
                                style={{
                                    background: 'linear-gradient(135deg, var(--themecolor), #2980b9)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontWeight: '600'
                                }}
                            >
                                <i className="fa fa-magic me-1"></i>Generate AI Calendar
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .calendar-grid {
                    grid-template-columns: repeat(7, 1fr) !important;
                }
                @media (max-width: 991px) {
                    .calendar-day {
                        min-height: 85px !important;
                        padding: 6px !important;
                    }
                    .calendar-day .badge {
                        padding: 1px 4px !important;
                    }
                    .calendar-day .badge span {
                        display: none !important;
                    }
                }
                @media (max-width: 575px) {
                    .calendar-grid {
                        gap: 4px !important;
                    }
                    .calendar-day {
                        min-height: 60px !important;
                        padding: 4px !important;
                    }
                    .calendar-day .fw-bold {
                        font-size: 11px !important;
                    }
                    .calendar-day .text-truncate {
                        display: none !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default MonthlyPostingPlan;
